import { Button, Input, notification, Select, Tooltip } from 'antd';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import styles from './index.scss';

const ThresholdCell = ({
  index,
  inputValue,
  inputType,
  handleInputChange,
  readonly
}) => (
  <div className={styles.thresholdCell}>
    <Tooltip placement="left" title={inputValue}>
      <Input
        className={classnames(styles.thresholdInput)}
        size="small"
        type={inputType}
        readOnly={readonly}
        value={inputValue}
        onChange={(e) => handleInputChange(index, e.target.value)}
        onWheel={(e) => e.target.blur()}
      />
    </Tooltip>
  </div>
);

ThresholdCell.propTypes = {
  index: PropTypes.number,
  inputValue: PropTypes.string,
  inputType: PropTypes.string,
  handleInputChange: PropTypes.func,
  readonly: PropTypes.bool
};

const getConditionOptions = (riskIndicators, indicator) => {
  const rule = riskIndicators.find((item) => item.riskIndicator === indicator);
  if (rule && Array.isArray(rule.condition))
    return rule.condition.map((cond) => (
      <Select.Option value={cond} key={cond}>
        {cond}
      </Select.Option>
    ));
  return [];
};

const getInputPattern = (
  riskIndicatorType,
  riskCondition = null,
  newRiskCondition = null
) => {
  if (riskCondition || newRiskCondition) {
    if (newRiskCondition === 'IN BETWEEN') return /^[0-9,. ]*$/;
    if (newRiskCondition === null && riskCondition === 'IN BETWEEN')
      return /^[0-9,. ]*$/;
  }

  switch (riskIndicatorType) {
    case 'Boolean':
      return /^([Tt][Rr][Uu][Ee]|[Ff][Aa][Ll][Ss][Ee])*$/;
    case 'Numeric':
      return /^\d{0,9}(\.\d{0,6})?$/;
    default:
      return /^[\p{L}\p{M}\p{N}\p{P}\p{S}\p{Z}]*$/u;
  }
};

export const getTableColumns = (
  selectedTab,
  riskIndicators,
  handleConditionChange,
  handleThresholdChange,
  handleWeightChange,
  handleDeleteRuleConfirm,
  isLoading,
  validationErrorMessage,
  isRenderingActionColumn
) =>
  isLoading
    ? []
    : selectedTab < 2 && isRenderingActionColumn
    ? [
        {
          title: 'Risk Indicator',
          dataIndex: 'riskIndicator'
        },
        {
          title: 'Condition',
          align: 'center',
          render: (text, record, index) => (
            <Select
              className={styles.conditionSelect}
              size="small"
              value={
                'newRiskCondition' in record
                  ? record.newRiskCondition
                  : record.riskCondition
              }
              onChange={(value) => handleConditionChange(index, value)}>
              {getConditionOptions(riskIndicators, record.riskIndicator)}
            </Select>
          )
        },
        {
          title: 'Value',
          align: 'center',
          render: (text, record, index) => (
            <>
              <ThresholdCell
                index={index}
                inputValue={
                  'newRiskValues' in record
                    ? record.newRiskValues
                    : record.riskValues.join(',')
                }
                inputType="text"
                handleInputChange={handleThresholdChange}
              />
              <span className={styles.error}>
                {validationErrorMessage[index]}
              </span>
            </>
          )
        },
        {
          title: 'Weight',
          align: 'center',
          render: (text, record, index) => (
            <ThresholdCell
              index={index}
              inputValue={
                'newRiskWeight' in record
                  ? record.newRiskWeight
                  : record.riskWeight
              }
              inputType="number"
              handleInputChange={handleWeightChange}
            />
          )
        },
        {
          title: 'Action',
          align: 'center',
          render: (text, record, index) => (
            <Button
              type="text"
              size="small"
              icon={<i className={styles.trashIcon} />}
              onClick={() => handleDeleteRuleConfirm(record)}
            />
          )
        }
      ]
    : selectedTab < 2 && !isRenderingActionColumn
    ? [
        {
          title: 'Risk Indicator',
          dataIndex: 'riskIndicator'
        },
        {
          title: 'Condition',
          align: 'center',
          render: (text, record, index) => (
            <Select
              className={styles.conditionSelect}
              size="small"
              value={
                'newRiskCondition' in record
                  ? record.newRiskCondition
                  : record.riskCondition
              }
              onChange={(value) => handleConditionChange(index, value)}>
              {getConditionOptions(riskIndicators, record.riskIndicator)}
            </Select>
          )
        },
        {
          title: 'Value',
          align: 'center',
          render: (text, record, index) => (
            <ThresholdCell
              index={index}
              inputValue={
                'newRiskValues' in record
                  ? record.newRiskValues
                  : record.riskValues.join(',')
              }
              inputType="text"
              handleInputChange={handleThresholdChange}
            />
          )
        },
        {
          title: 'Weight',
          align: 'center',
          render: (text, record, index) => (
            <ThresholdCell
              index={index}
              inputValue={
                'newRiskWeight' in record
                  ? record.newRiskWeight
                  : record.riskWeight
              }
              inputType="number"
              handleInputChange={handleWeightChange}
            />
          )
        }
      ]
    : selectedTab === 3
    ? [
        {
          title: 'Risk Indicator',
          dataIndex: 'riskIndicator'
        },
        {
          title: 'Importance %',
          align: 'center',
          render: (text, record, index) => (
            <ThresholdCell
              readonly
              inputValue={Number(record.riskWeight).toFixed(2)}
              inputType="text"
            />
          )
        }
      ]
    : [
        {
          title: 'Risk Indicator',
          dataIndex: 'riskIndicator'
        },
        {
          title: 'Threshold',
          align: 'center',
          render: (text, record, index) => (
            <ThresholdCell
              readonly
              inputValue={Number(record.riskWeight).toFixed(2)}
              inputType="text"
            />
          )
        }
      ];

export const handleAddRuleNotification = (data) => {
  if (data.msg) {
    notification.open({
      message: (
        <p>
          <i style={{ color: '#e74c3c' }} className="fa fa-close"></i>
          &nbsp;&nbsp;Error
        </p>
      ),
      description: data.msg
    });
  } else {
    notification.open({
      message: (
        <p>
          <i style={{ color: '#2ecc71' }} className="fa fa-check"></i>
          &nbsp;&nbsp;Success
        </p>
      ),
      description: 'Rule has been saved.'
    });
  }
};

export const handlePostTriggerNotification = (pipelineId, msg) => {
  let title = (
    <p>
      <i
        style={{ color: '#2ecc71', marginRight: '10px' }}
        className="fa fa-check"></i>
      Success
    </p>
  );
  if (!pipelineId) {
    title = (
      <p>
        <i
          style={{ color: '#e74c3c', marginRight: '10px' }}
          className="fa fa-close"></i>
        Error
      </p>
    );

    if (!msg) {
      msg = 'Something went wrong. please try again';
    }
  }

  notification.open({
    message: title,
    description: msg ?? 'Re-risk scoring has been triggered.'
  });
};

export const handleUpdateRuleNotification = (msg) => {
  let title = (
    <p>
      <i
        style={{ color: '#2ecc71', marginRight: '10px' }}
        className="fa fa-check"></i>
      Success
    </p>
  );
  if (msg) {
    title = (
      <p>
        <i
          style={{ color: '#e74c3c', marginRight: '10px' }}
          className="fa fa-close"></i>
        Error
      </p>
    );
  }
  notification.open({
    message: title,
    description: msg ?? 'Rules have been updated.'
  });
};

export const handleDeleteRuleNotification = (data) => {
  if (data) {
    notification.open({
      message: (
        <p>
          <i style={{ color: '#e74c3c' }} className="fa fa-close"></i>
          &nbsp;&nbsp;Error
        </p>
      ),
      description: data
    });
  } else {
    notification.open({
      message: (
        <p>
          <i style={{ color: '#2ecc71' }} className="fa fa-check"></i>
          &nbsp;&nbsp;Success
        </p>
      ),
      description: 'Rule has been deleted.'
    });
  }
};

export const getRiskIndicatorUpdates = (data, riskIndicators) => {
  let validationError = false;
  let errorMsg = {};
  let res = data
    .filter(
      (indicator) =>
        ('newRiskCondition' in indicator &&
          indicator.newRiskCondition !== indicator.riskCondition) ||
        ('newRiskValues' in indicator &&
          indicator.newRiskValues !== indicator.riskValues.join(',')) ||
        ('newRiskWeight' in indicator &&
          indicator.newRiskWeight !== indicator.riskWeight)
    )
    .map((indicator) => {
      const {
        uuid,
        riskCondition,
        riskValues,
        riskWeight,
        newRiskCondition,
        newRiskValues,
        newRiskWeight,
        category: ruleCategory,
        riskIndicator
      } = indicator;
      const updated = {
        uuid,
        riskCondition,
        riskValue: riskValues.join(','),
        riskWeight,
        ruleCategory,
        riskIndicator
      };

      const rule = riskIndicators.find(
        (item) => item.riskIndicator === riskIndicator
      );
      const index = data.findIndex((item) => item.uuid === uuid);

      if ('newRiskCondition' in indicator) {
        if (rule?.riskIndicatorType) {
          let pattern = new RegExp(
            getInputPattern(
              rule.riskIndicatorType,
              indicator.riskCondition,
              indicator?.newRiskCondition
            )
          );
          if (
            !indicator.newRiskValues &&
            !pattern.test(indicator.riskValues.join(','))
          ) {
            validationError = true;
            errorMsg = {
              ...errorMsg,
              [index]: `Only ${rule?.riskIndicatorType} values allowed`
            };
          } else {
            errorMsg = {
              ...errorMsg,
              [index]: ''
            };
          }
        }

        updated.riskCondition = newRiskCondition;
      }
      if ('newRiskValues' in indicator) {
        if (rule?.riskIndicatorType) {
          let pattern = new RegExp(
            getInputPattern(
              rule.riskIndicatorType,
              riskCondition,
              newRiskCondition
            )
          );
          if (pattern.test(newRiskValues)) {
            updated.riskValue = newRiskValues;
            errorMsg = {
              ...errorMsg,
              [index]: ''
            };
          } else {
            validationError = true;
            errorMsg = {
              ...errorMsg,
              [index]: `Only ${rule?.riskIndicatorType} values allowed`
            };
          }
        } else {
          updated.riskValue = newRiskValues;
        }
      }
      if ('newRiskWeight' in indicator) updated.riskWeight = newRiskWeight;
      return updated;
    });

  return { indicatorsUpdates: validationError ? [] : res, errorMsg };
};

export const getStaticProperty = (properties, propertyName) => {
  if (Array.isArray(properties)) {
    const found = properties.find((p) => p.staticProperty === propertyName);
    if (found) return found.staticValue;
  }
};

export const isValidRiskIndicatorValue = (value) => {
  // allow text/numeric/comma/dot/dash/space, cannot start with special char
  // eslint-disable-next-line no-useless-escape
  const re = /^[\p{L}\p{N}\p{M}]([\p{L}\p{N}\p{M}\s\-,.])*$/u;
  return re.test(value);
};

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
